import { updateDoc, type DocumentReference } from 'firebase/firestore';
import { ApiError, apiPost } from './apiClient';
import { trackEvent } from './analyticsService';
import type { StepIngredientMap } from '../types/recipe';

export interface StepMapRequest {
  title: string;
  /** The ingredient lines exactly as saved on the document, in order. */
  ingredients: string[];
  /** The step texts exactly as saved on the document, in order. */
  steps: string[];
}

/**
 * Asks the server which ingredients each step uses (#857).
 *
 * Returns the rows to store as `stepIngredients`, or null when the model had
 * no answer (`mapStepIngredients` returned null): the recipe then stays on the
 * string matcher, which is what it had before. Throws on a transport or auth
 * failure so the caller can say why.
 */
export async function requestStepIngredientMap(input: StepMapRequest): Promise<StepIngredientMap[] | null> {
  const data = await apiPost<{ success: true; stepIngredients: StepIngredientMap[] | null }>(
    '/api/recipes/import',
    { action: 'map-steps', ...input },
    { requireSuccess: true },
  );
  return Array.isArray(data.stepIngredients) ? data.stepIngredients : null;
}

/**
 * Gives a recipe the app just saved its step→ingredient map.
 *
 * The Add Recipe sheet writes the document itself and never went through the
 * server import, so until #857 nothing it saved carried a map and the chips
 * came from the ~85% string matcher. This runs after the save, off the path
 * the cook is waiting on: a map that lands a few seconds later is still there
 * for the first cook, and a map that never lands leaves the recipe exactly as
 * it was. Never throws; every outcome is one `step_map_at_save` event.
 */
export async function attachStepIngredientMap(
  recipeRef: DocumentReference,
  input: StepMapRequest,
  source: string,
): Promise<StepIngredientMap[] | null> {
  if (!input.ingredients.length || !input.steps.length) return null;
  try {
    const map = await requestStepIngredientMap(input);
    if (!map) {
      trackEvent('step_map_at_save', { source, ok: false, reason: 'no-map' });
      return null;
    }
    await updateDoc(recipeRef, { stepIngredients: map });
    trackEvent('step_map_at_save', {
      source,
      ok: true,
      step_count: map.length,
      chip_count: map.reduce((n, row) => n + row.ingredients.length, 0),
    });
    return map;
  } catch (err) {
    // Expected offline, with the model down, or once the day's AI allowance
    // is spent (429); the chips fall back to the matcher, so this is a replay
    // note, not an issue. The quota case is counted apart: a rising share
    // means the ceiling, not the model, is what keeps maps off saved recipes.
    console.warn('[stepIngredientMap] not attached:', (err as Error)?.message);
    const reason = err instanceof ApiError && err.status === 429 ? 'quota' : 'failed';
    trackEvent('step_map_at_save', { source, ok: false, reason });
    return null;
  }
}
